<template>
  <div class="service-add">
    <ai-card class="service-add__card" :radius="16">
      <div style="position: relative;" :style="{height: props.isRemoveBg ? 'calc(100% - 56px)' : '100%'}" >
        <ai-drop-loader v-if="loading" />
        <ai-file-upload
          :key="key"
          class="service-add__input mb-4"
          :title="$t('services.add.file-input-title')"
          :description="$t('services.add.file-input-description')"
          v-model="fileForUpload"
          @keyup.prevent
        >
        <template v-slot:top>
          <slot />
        </template>
         <ai-button theme="gradient-primary" style="margin: 16px auto 0; display: block">
          {{ $t('services.add.upload-from-pc') }}
         </ai-button>
        </ai-file-upload>
      </div>
      <div v-if="props.isRemoveBg || props.isFinetuneBg" class="d-flex remove-bg-switch">
        <v-spacer />
        <div v-if="props.isFinetuneBg" class="d-flex align-center">
          <div class="remove-bg-switch__text">
            {{ $t('editor.add-product.finetune-bg-switch') }}
          </div>
          <v-switch v-model="finetuneBg" hide-details />
        </div>
        <div  v-if="props.isRemoveBg"  class="d-flex align-center">
          <div class="remove-bg-switch__text">
            {{ $t('editor.add-product.remove-bg-switch') }}
          </div>
          <v-switch v-model="removeBg" hide-details />
        </div>
      </div>
    </ai-card>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useEventBus } from '@vueuse/core';

import { useCommonStore } from '@/store/common';
import { useServicesStore } from '@/store/services';

import { removeBase64Prefix } from '@/helpers/removeBase64Prefix';

import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';
import AiFileUpload from '@/components/UI/AiFileUpload/AiFileUpload.vue';

const props = defineProps({
  isRemoveBg: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isFinetuneBg: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  finetuneValue: {
    type: Boolean as PropType<boolean>,
  },
});

const maxFileSize = 5 * 1024 * 1024;

const { t } = useI18n();

const busError = useEventBus<string>('toast-error');

const emit = defineEmits(['update:finetuneBg']);

const { currentService } = storeToRefs(useCommonStore());

const { generateImage } = useServicesStore();

const { imageForActions, loading, sourceBgImage } = storeToRefs(useServicesStore());

const key = ref<number>(0);
const fileForUpload = ref<File>();
const removeBg = ref<boolean>(true);
const finetuneBg = ref<boolean>(false);

const uploadFile = async (file: File): Promise<void> => {
  if (!file.type.match('image.(jpeg|png|webp)')) {
    busError.emit(t('editor.add-product.err-msg-pls-select-img'));
    fileForUpload.value = undefined;
    return;
  }
  if (file.size > maxFileSize) {
    busError.emit(t('editor.add-product.err-msg-pls-select-small-img'));
    fileForUpload.value = undefined;
    return;
  }
  loading.value = true;
  const reader = new FileReader();
  reader.onload = async (e: any) => {
    const image = e.target.result;

    if ((props.isRemoveBg && removeBg.value) || currentService.value === 'remove_background') {
      const res = await generateImage({
        action: 'remove_background',
        image: removeBase64Prefix(image),
        params: {},
      });

      if (currentService.value === 'create_background_for_image' || currentService.value === 'shadow') {
        imageForActions.value = `data:image/png;base64,${res.image}`;
      } else {
        imageForActions.value = `data:image/png;base64,${res.payload.notTrimmedImage}`;
      }

      if (currentService.value === 'remove_background' || currentService.value === 'create_background_for_image') {
        sourceBgImage.value = image;
      }
    } else {
      imageForActions.value = image;
    }
    fileForUpload.value = undefined;
    key.value += 1;
    loading.value = false;
  };
  reader.readAsDataURL(file);
};

watch(fileForUpload, (newVal) => {
  if (!newVal) {
    return;
  }
  uploadFile(newVal);
});

watch(finetuneBg, () => {
  emit('update:finetuneBg', finetuneBg.value);
});
</script>

<style scoped lang="scss" src="./AddImage.scss" />
